import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button, Input } from '@artosera/ui';
import {
  useListeRegistre,
  useVerificationRegistre,
  registrePoliceApi,
  type FiltresRegistre,
} from '../../api/registrePolice.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { enregistrerFichier, ouvrirUrlSignee } from '../../api/client.js';
import { estApiError } from '../../api/errors.js';
import { Field } from '../Field.js';
import styles from './RegistrePolice.module.css';

export function ListeRegistre({
  galerieSlug,
  archive = false,
  controle = false,
  peutLire = true,
}: {
  galerieSlug: string;
  archive?: boolean;
  controle?: boolean;
  peutLire?: boolean;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const [saisie, setSaisie] = useState<FiltresRegistre>({});
  const [filtres, setFiltres] = useState<FiltresRegistre>({ page: 1, parPage: 50 });
  const [exportEnCours, setExportEnCours] = useState(false);
  const [erreurExport, setErreurExport] = useState('');
  const liste = useListeRegistre(galerieSlug, filtres, archive, peutLire);
  const verification = useVerificationRegistre(galerieSlug, archive, peutLire);
  const label = (cle: string) => t(`regie:registrePolice.${cle}`);
  const date = (valeur: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' }).format(
      new Date(valeur),
    );
  async function exporter(format: 'pdf' | 'csv') {
    setErreurExport('');
    setExportEnCours(true);
    const periode = { du: filtres.du, au: filtres.au };
    try {
      if (format === 'pdf') {
        const resultat = await dansLaPorteeCourante((appel) =>
          registrePoliceApi.pdf(galerieSlug, periode, archive, appel),
        );
        ouvrirUrlSignee(resultat.url);
      } else {
        const resultat = await dansLaPorteeCourante((appel) =>
          registrePoliceApi.csv(galerieSlug, periode, archive, appel),
        );
        enregistrerFichier(resultat, 'registre-police.csv');
      }
    } catch (erreur) {
      setErreurExport(estApiError(erreur) ? erreur.message : t('commun:erreurReseau'));
    } finally {
      setExportEnCours(false);
    }
  }
  if (!peutLire) return <p role="alert">{label('refus')}</p>;
  return (
    <>
      <div
        className={styles.verification}
        role={verification.data?.ok === false ? 'alert' : 'status'}
      >
        {verification.isPending && t('commun:chargement')}
        {verification.isError && <p>{label('verificationErreur')}</p>}
        {verification.data && (
          <p>
            {verification.data.ok
              ? label('chaineValide')
              : t('regie:registrePolice.chaineRompue', {
                  numero: verification.data.rupture?.numero,
                })}
          </p>
        )}
        <Button
          variant="ghost"
          onClick={() => void verification.refetch()}
          disabled={verification.isFetching}
        >
          {label('verifier')}
        </Button>
      </div>
      <form
        className={styles.filtres}
        data-ui="filters"
        data-lecture-seule="ok"
        onSubmit={(event) => {
          event.preventDefault();
          setFiltres({ ...saisie, page: 1, parPage: 50 });
        }}
      >
        {(['numero', 'du', 'au', 'vendeur', 'artiste', 'inventaire'] as const).map((cle) => (
          <Field key={cle} htmlFor={`registre-${cle}`} label={label(`recherche.${cle}`)}>
            <Input
              id={`registre-${cle}`}
              type={cle === 'du' || cle === 'au' ? 'date' : 'text'}
              inputMode={cle === 'numero' ? 'numeric' : undefined}
              value={saisie[cle] ?? ''}
              onChange={(event) => setSaisie({ ...saisie, [cle]: event.target.value })}
            />
          </Field>
        ))}
        <Button type="submit">{label('rechercher')}</Button>
      </form>
      {!controle && (
        <div className={styles.actions}>
          <Button disabled={exportEnCours} onClick={() => void exporter('pdf')}>
            {label('exportPdf')}
          </Button>
          <Button disabled={exportEnCours} onClick={() => void exporter('csv')}>
            {label('exportCsv')}
          </Button>
          <p>{label('exportsPeriode')}</p>
          {erreurExport && <p role="alert">{erreurExport}</p>}
        </div>
      )}
      {liste.isPending && <p role="status">{t('commun:chargement')}</p>}
      {liste.isError && (
        <p role="alert">
          {estApiError(liste.error) ? liste.error.message : t('commun:erreurReseau')}
        </p>
      )}
      {liste.data?.inscriptions.length === 0 && <p>{label('aucune')}</p>}
      {liste.data && (
        <div className={styles.tableauEnveloppe}>
          <table className={styles.tableau} data-density="registre">
            <thead>
              <tr>
                {['numero', 'date', 'nature', 'objet', 'vendeur', 'artiste', 'inventaire'].map(
                  (cle) => (
                    <th key={cle} scope="col">
                      {label(`colonnes.${cle}`)}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {liste.data.inscriptions.map((entree) => (
                <tr key={entree.numero}>
                  <td>
                    {controle || archive ? (
                      entree.numero
                    ) : (
                      <Link to={`/${galerieSlug}/registre-police/${entree.numero}`}>
                        {entree.numero}
                      </Link>
                    )}
                  </td>
                  <td>{date(entree.valideLe)}</td>
                  <td>{label(`natures.${entree.nature}`)}</td>
                  <td>{entree.objet.titre || entree.objet.nature}</td>
                  <td>
                    {entree.vendeur.raisonSociale ||
                      [entree.vendeur.prenom, entree.vendeur.nom].filter(Boolean).join(' ')}
                  </td>
                  <td>{entree.objet.artiste ?? '—'}</td>
                  <td>{entree.objet.numeroInventaire ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {liste.data && (
        <div className={styles.actions}>
          <Button
            disabled={liste.isFetching || liste.data.pagination.page <= 1}
            onClick={() => setFiltres({ ...filtres, page: (filtres.page ?? 1) - 1 })}
          >
            {label('precedent')}
          </Button>
          <span>
            {t('regie:registrePolice.pagination', {
              page: liste.data.pagination.page,
              pages: Math.max(1, liste.data.pagination.pages),
              total: liste.data.pagination.total,
            })}
          </span>
          <Button
            disabled={liste.isFetching || liste.data.pagination.page >= liste.data.pagination.pages}
            onClick={() => setFiltres({ ...filtres, page: (filtres.page ?? 1) + 1 })}
          >
            {label('suivant')}
          </Button>
        </div>
      )}
    </>
  );
}
